/**
 * Synthesis helpers (tone / noise / fm + Kit) for stage-registered custom sounds and the
 * ambient beds. The game's own SFX are recorded samples (samples.ts); these helpers remain
 * so stages can still register() procedural sounds.
 */
import { clamp, type NoiseColor } from './dsp';

export interface Kit {
  ctx: AudioContext;
  /** Dry output (already spatialised/gained). */
  out: AudioNode;
  /** Reverb send input. */
  rev: AudioNode;
  /** Start time. */
  t: number;
  /** Pitch multiplier (includes random variance). */
  p: number;
  /** Volume multiplier. */
  v: number;
  noise(color: NoiseColor): AudioBuffer;
  /** Low-CPU profile (mobile music): instruments skip secondary partials. */
  lite?: boolean;
}

interface ToneOpts {
  type?: OscillatorType;
  f: number;
  /** Glide target frequency. */
  to?: number;
  /** Glide duration (defaults to dur). */
  glide?: number;
  t0?: number;
  dur: number;
  a?: number;
  d?: number;
  s?: number;
  r?: number;
  vol?: number;
  detune?: number;
  lp?: number;
  lpEnd?: number;
  hp?: number;
  bp?: number;
  q?: number;
  /** Vibrato depth (cents) & rate (Hz). */
  vib?: number;
  vibRate?: number;
  /** Reverb send 0..1. */
  rev?: number;
  /** Extra waveshaping drive (>1). */
  drive?: number;
}

interface NoiseOpts {
  color?: NoiseColor;
  t0?: number;
  dur: number;
  a?: number;
  d?: number;
  s?: number;
  r?: number;
  vol?: number;
  lp?: number;
  lpEnd?: number;
  hp?: number;
  bp?: number;
  bpEnd?: number;
  q?: number;
  rev?: number;
}

function envelope(g: AudioParam, t0: number, peak: number, a: number, d: number, s: number, r: number, dur: number): number {
  g.setValueAtTime(0.0001, t0);
  g.linearRampToValueAtTime(peak, t0 + a);
  const sus = Math.max(0.0001, peak * s);
  g.exponentialRampToValueAtTime(sus, t0 + a + d);
  const end = t0 + Math.max(a + d, dur);
  g.setValueAtTime(sus, end);
  g.exponentialRampToValueAtTime(0.0001, end + r);
  return end + r;
}

const driveCurves = new Map<number, Float32Array<ArrayBuffer>>();
function driveCurve(drive: number): Float32Array<ArrayBuffer> {
  let c = driveCurves.get(drive);
  if (!c) {
    c = new Float32Array(257);
    for (let i = 0; i < 257; i++) {
      const x = (i / 256) * 2 - 1;
      c[i] = Math.tanh(x * drive);
    }
    driveCurves.set(drive, c);
  }
  return c;
}

function filterChain(ctx: AudioContext, o: ToneOpts | NoiseOpts, t0: number, end: number): { input: AudioNode; output: AudioNode } | null {
  const nodes: BiquadFilterNode[] = [];
  if (o.lp) {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.Q.value = o.q ?? 0.7;
    f.frequency.setValueAtTime(o.lp, t0);
    if (o.lpEnd) f.frequency.exponentialRampToValueAtTime(Math.max(30, o.lpEnd), end);
    nodes.push(f);
  }
  if (o.hp) {
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.Q.value = 0.7;
    f.frequency.value = o.hp;
    nodes.push(f);
  }
  if (o.bp) {
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = o.q ?? 1;
    f.frequency.setValueAtTime(o.bp, t0);
    const bpEnd = (o as NoiseOpts).bpEnd;
    if (bpEnd) f.frequency.exponentialRampToValueAtTime(Math.max(30, bpEnd), end);
    nodes.push(f);
  }
  if (!nodes.length) return null;
  for (let i = 0; i < nodes.length - 1; i++) nodes[i].connect(nodes[i + 1]);
  return { input: nodes[0], output: nodes[nodes.length - 1] };
}

/** Oscillator voice with ADSR, optional glide/vibrato/filter/drive/reverb. Returns end time. */
export function tone(k: Kit, o: ToneOpts): number {
  const ctx = k.ctx;
  const t0 = o.t0 ?? k.t;
  const a = o.a ?? 0.005, d = o.d ?? 0.05, s = o.s ?? 0.6, r = o.r ?? 0.08;
  const osc = ctx.createOscillator();
  osc.type = o.type ?? 'sine';
  const f0 = clamp(o.f * k.p, 20, 20000);
  osc.frequency.setValueAtTime(f0, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(clamp(o.to * k.p, 20, 20000), t0 + (o.glide ?? o.dur));
  if (o.detune) osc.detune.value = o.detune;
  const g = ctx.createGain();
  const end = envelope(g.gain, t0, (o.vol ?? 0.5) * k.v, a, d, s, r, o.dur);
  let head: AudioNode = osc;
  if (o.vib) {
    const lfo = ctx.createOscillator();
    lfo.frequency.value = o.vibRate ?? 6;
    const lg = ctx.createGain();
    lg.gain.value = o.vib;
    lfo.connect(lg).connect(osc.detune);
    lfo.start(t0);
    lfo.stop(end + 0.05);
  }
  if (o.drive && o.drive > 1) {
    const ws = ctx.createWaveShaper();
    ws.curve = driveCurve(o.drive);
    head.connect(ws);
    head = ws;
  }
  const fc = filterChain(ctx, o, t0, end);
  if (fc) {
    head.connect(fc.input);
    head = fc.output;
  }
  head.connect(g);
  g.connect(k.out);
  if (o.rev) {
    const rs = ctx.createGain();
    rs.gain.value = o.rev;
    g.connect(rs).connect(k.rev);
  }
  osc.start(t0);
  osc.stop(end + 0.05);
  return end;
}

/** Noise voice with ADSR + filters. Returns end time. */
export function noise(k: Kit, o: NoiseOpts): number {
  const ctx = k.ctx;
  const t0 = o.t0 ?? k.t;
  const a = o.a ?? 0.003, d = o.d ?? 0.04, s = o.s ?? 0.5, r = o.r ?? 0.05;
  const src = ctx.createBufferSource();
  src.buffer = k.noise(o.color ?? 'white');
  src.loop = true;
  src.loopStart = 0;
  src.loopEnd = src.buffer.duration;
  const g = ctx.createGain();
  const end = envelope(g.gain, t0, (o.vol ?? 0.4) * k.v, a, d, s, r, o.dur);
  let head: AudioNode = src;
  const fc = filterChain(ctx, o, t0, end);
  if (fc) {
    head.connect(fc.input);
    head = fc.output;
  }
  head.connect(g).connect(k.out);
  if (o.rev) {
    const rs = ctx.createGain();
    rs.gain.value = o.rev;
    g.connect(rs).connect(k.rev);
  }
  src.start(t0, Math.random() * (src.buffer.duration - 0.5));
  src.stop(end + 0.05);
  return end;
}

/** Simple 2-op FM bell / pluck. */
export function fm(k: Kit, o: { f: number; ratio?: number; index?: number; t0?: number; dur: number; vol?: number; a?: number; r?: number; rev?: number; type?: OscillatorType }): number {
  const ctx = k.ctx;
  const t0 = o.t0 ?? k.t;
  const f = Math.min(12000, o.f * k.p);
  const car = ctx.createOscillator();
  car.type = o.type ?? 'sine';
  car.frequency.value = f;
  const mod = ctx.createOscillator();
  mod.frequency.value = Math.min(20000, f * (o.ratio ?? 2.01));
  const mg = ctx.createGain();
  mg.gain.setValueAtTime(f * (o.index ?? 2), t0);
  mg.gain.exponentialRampToValueAtTime(f * 0.05, t0 + o.dur);
  mod.connect(mg).connect(car.frequency);
  const g = ctx.createGain();
  const end = envelope(g.gain, t0, (o.vol ?? 0.4) * k.v, o.a ?? 0.004, o.dur * 0.6, 0.25, o.r ?? o.dur * 0.6, o.dur * 0.4);
  car.connect(g).connect(k.out);
  if (o.rev) {
    const rs = ctx.createGain();
    rs.gain.value = o.rev;
    g.connect(rs).connect(k.rev);
  }
  car.start(t0);
  mod.start(t0);
  car.stop(end + 0.05);
  mod.stop(end + 0.05);
  return end;
}
